import { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import { MakeApiCall } from "../../../../apis";
import { ConvertParams } from "../../../../config";
import { extractSeries, sumSeries, pickRowNumber } from "./pickSeriesFromRows";

function normalizeChartRows(data, periodicity) {
  let list = [];
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.data)) list = data.data;
  if (periodicity !== "month") list = list.slice();
  return list;
}

function normalizeSummaryPayload(data, periodicity) {
  let rows = [];
  if (Array.isArray(data?.summaryData)) rows = data.summaryData;
  else if (data?.summaryData && typeof data.summaryData === "object") rows = [data.summaryData];
  else if (Array.isArray(data)) rows = data;
  else if (data && typeof data === "object" && data.net_profit != null) rows = [data];
  if (periodicity !== "month") rows = rows.slice();

  return {
    rows,
    labels: data?.label || {},
    compareSummary: data?.compare_with?.summaryData || null,
    compareChange: data?.compare_with?.change_percent || null,
  };
}

function getChangePct(changes, keys) {
  if (!changes) return null;
  const list = Array.isArray(changes) ? changes : [changes];
  for (const row of list) {
    for (const key of keys) {
      const v = row?.[key];
      if (v != null && Number.isFinite(Number(v))) return Number(v);
    }
  }
  return null;
}

function compareTrend(previous, changes, keys) {
  const pct = getChangePct(changes, keys);
  return {
    // UI shows the compare period value before the percentage (e.g. "₹8,130 ↑455.6%").
    delta: Number(previous) || 0,
    pct,
  };
}

const SALES_LABEL_TO_SUMMARY = {
  organic: "organic",
  sponsored_products: "sp_sales",
  sponsored_brands: "sb_sales",
  sponsored_display: "sd_sales"
};

const PPC_LABEL_TO_COST = {
  sponsored_products_ppc: "sp_cost",
  sponsored_brands_ppc: "sb_cost",
  sponsored_display_ppc: "sd_cost",
};

const HIDDEN_PPC_LABEL_KEYS = new Set(["sponsored_brands_video_ppc"]);
const HIDDEN_PPC_COST_KEYS = new Set(["sbv_cost"]);

const AD_CLICK_LABELS = {
  sp_click: "Sponsored Products",
  sb_click: "Sponsored Brands",
  sd_click: "Sponsored Display",
};

function humanizeMetricKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function stripPpcSuffix(title) {
  return String(title || "")
    .replace(/\bppc\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function amazonFeePaths(labelChildKey) {
  const alias = {
    commission: ["referral_fee", "commission"],
    fba_per_unit_fulfillment_fee: ["fba_per_unit_fulfillment_fee", "fba_per_unit_fulfilment_fee"],
    fba_per_order_fulfillment_fee: ["fba_per_order_fulfillment_fee", "fba_per_order_fulfilment_fee"],
    gift_wrap_charge_back: ["gift_wrap_charge_back", "giftwrap_chargeback"],
    shipping_charge_back: ["shipping_charge_back", "shipping_chargeback"],
  };
  const extra = alias[labelChildKey];
  return [
    ...(extra || []),
    labelChildKey,
    labelChildKey.replace(/fulfillment/g, "fulfilment"),
    labelChildKey.replace(/fulfilment/g, "fulfillment"),
  ];
}

function amazonFeeLineValue(obj, labelChildKey) {
  if (!obj) return 0;
  return pickRowNumber(obj, amazonFeePaths(labelChildKey));
}

function hasAmazonFeeKey(obj, labelChildKey) {
  if (!obj || typeof obj !== "object") return false;
  return amazonFeePaths(labelChildKey).some((p) => Object.prototype.hasOwnProperty.call(obj, p));
}

function plSalesChildren(summaryLabels, summary, compareSummary) {
  const meta = summaryLabels?.sales?.children;
  const d = summary?.sales;
  const cd = compareSummary?.sales;
  if (!meta || !d) return [];
  const out = [];
  for (const [labelKey, m] of Object.entries(meta)) {
    const sk = SALES_LABEL_TO_SUMMARY[labelKey];
    if (!sk) continue;
    const v = pickRowNumber(d, [sk]);
    out.push({
      key: labelKey,
      title: m.title,
      type: m.type || "currency",
      value: v,
      compareDelta: cd ? pickRowNumber(cd, [sk]) : null,
    });
  }
  return out;
}

function plAmazonFeesChildren(summaryLabels, summary, compareSummary) {
  const meta = summaryLabels?.fba_fees?.children;
  const d = summary?.amazon_fees;
  const cd = compareSummary?.amazon_fees;
  if (!d) return [];
  const out = [];
  const consumed = new Set();
  if (meta) {
    for (const [labelKey, m] of Object.entries(meta)) {
      const paths = amazonFeePaths(labelKey);
      const matched = paths.find((p) => Object.prototype.hasOwnProperty.call(d, p));
      if (!matched) continue;
      paths.forEach((p) => {
        if (Object.prototype.hasOwnProperty.call(d, p)) consumed.add(p);
      });
      out.push({
        key: labelKey,
        title: m.title,
        type: m.type || "currency",
        value: amazonFeeLineValue(d, labelKey),
        compareDelta: cd ? amazonFeeLineValue(cd, labelKey) : null,
      });
    }
  }
  for (const k of Object.keys(d)) {
    if (k === "total" || consumed.has(k)) continue;
    out.push({
      key: k,
      title: humanizeMetricKey(k),
      type: "currency",
      value: pickRowNumber(d, [k]),
      compareDelta: cd ? pickRowNumber(cd, [k]) : null,
    });
  }
  return out;
}

function plPpcCostChildren(summaryLabels, summary, compareSummary) {
  const meta = summaryLabels?.ppc_costs?.children;
  const d = summary?.advertising_cost;
  const cd = compareSummary?.advertising_cost;
  if (!d) return [];
  const out = [];
  if (meta) {
    for (const [labelKey, m] of Object.entries(meta)) {
      if (HIDDEN_PPC_LABEL_KEYS.has(labelKey)) continue;
      const sk = PPC_LABEL_TO_COST[labelKey] || labelKey;
      const v = pickRowNumber(d, [sk]);
      out.push({
        key: labelKey,
        title: stripPpcSuffix(m.title),
        type: m.type || "currency",
        value: v,
        compareDelta: cd ? pickRowNumber(cd, [sk]) : null,
      });
    }
  }
  if (!out.length) {
    for (const k of Object.keys(d)) {
      if (k === "total" || HIDDEN_PPC_COST_KEYS.has(k)) continue;
      out.push({
        key: k,
        title: stripPpcSuffix(humanizeMetricKey(k)),
        type: "currency",
        value: pickRowNumber(d, [k]),
        compareDelta: cd ? pickRowNumber(cd, [k]) : null,
      });
    }
  }
  return out;
}

function plAdClicksChildren(summary, compareSummary) {
  const d = summary?.advertising_clicks;
  const cd = compareSummary?.advertising_clicks;
  if (!d) return [];
  const out = [];
  for (const [k, title] of Object.entries(AD_CLICK_LABELS)) {
    if (!(k in d)) continue;
    const v = pickRowNumber(d, [k]);
    out.push({
      key: k,
      title,
      type: "number",
      value: v,
      compareDelta: cd ? pickRowNumber(cd, [k]) : null,
    });
  }
  for (const k of Object.keys(d)) {
    if (k === "total" || AD_CLICK_LABELS[k]) continue;
    out.push({
      key: k,
      title: humanizeMetricKey(k),
      type: "number",
      value: pickRowNumber(d, [k]),
      compareDelta: cd ? pickRowNumber(cd, [k]) : null,
    });
  }
  return out;
}

function plRefundCostChildren(summaryLabels, summary, compareSummary) {
  const d = summary?.refund_cost;
  const cd = compareSummary?.refund_cost;
  const meta = summaryLabels?.refund_cost?.children;
  if (!d) return [];
  const out = [];
  for (const k of Object.keys(d)) {
    if (k === "total") continue;
    const m = meta?.[k];
    out.push({
      key: k,
      title: m?.title || humanizeMetricKey(k),
      type: m?.type || "currency",
      value: pickRowNumber(d, [k]),
      compareDelta: cd ? pickRowNumber(cd, [k]) : null,
    });
  }
  return out;
}

function plCogsChildren(summaryLabels, summary, compareSummary) {
  const d = summary?.cost_of_goods;
  const cd = compareSummary?.cost_of_goods;
  const meta = summaryLabels?.total_cogs?.children;
  if (!d) return [];
  const out = [];
  if (meta) {
    for (const [k, m] of Object.entries(meta)) {
      if (k === "total") continue;
      const v = pickRowNumber(d, [k]);
      out.push({
        key: k,
        title: m.title,
        type: m.type || "currency",
        value: v,
        compareDelta: cd ? pickRowNumber(cd, [k]) : null,
      });
    }
  }
  if (!out.length) {
    for (const k of Object.keys(d)) {
      if (k === "total") continue;
      out.push({
        key: k,
        title: humanizeMetricKey(k),
        type: "currency",
        value: pickRowNumber(d, [k]),
        compareDelta: cd ? pickRowNumber(cd, [k]) : null,
      });
    }
  }
  return out;
}

function plExpensesChildren(summaryLabels, summary, compareSummary) {
  const d = summary?.expenses;
  const cd = compareSummary?.expenses;
  const meta = summaryLabels?.expenses?.children;
  if (!d) return [];
  const out = [];
  if (meta) {
    for (const [k, m] of Object.entries(meta)) {
      if (k === "total") continue;
      const v = pickRowNumber(d, [k]);
      out.push({
        key: k,
        title: m.title,
        type: m.type || "currency",
        value: v,
        compareDelta: cd ? pickRowNumber(cd, [k]) : null,
      });
    }
  }
  if (!out.length) {
    for (const k of Object.keys(d)) {
      if (k === "total") continue;
      out.push({
        key: k,
        title: humanizeMetricKey(k),
        type: "currency",
        value: pickRowNumber(d, [k]),
        compareDelta: cd ? pickRowNumber(cd, [k]) : null,
      });
    }
  }
  return out;
}

function hasTaxLiability(summary) {
  const tl = summary?.tax_liability;
  return tl && typeof tl === "object" && !Array.isArray(tl);
}

function plTaxLiabilityChildren(summaryLabels, summary, compareSummary, isTaxInputCreditRecoverable = false) {
  const tl = summary?.tax_liability;
  const ctl = compareSummary?.tax_liability;
  if (!hasTaxLiability(summary)) return [];
  const taxTitles = summaryLabels?.tax?.children || {};
  const orderTaxTitle = taxTitles.order_tax?.title || "Order Tax";

  const push = (key, title, valueKey, compareKeys = [valueKey]) => {
    const v = pickRowNumber(tl, [valueKey]);
    if (!Number.isFinite(v)) return null;
    return {
      key,
      title,
      type: "currency",
      value: v,
      compareDelta: ctl ? pickRowNumber(ctl, compareKeys) : null,
    };
  };

  const orderTax = push("total_tax_collected", orderTaxTitle, "total_tax_collected");

  if (!isTaxInputCreditRecoverable) {
    return orderTax ? [orderTax] : [];
  }

  const paidItem = push("total_tax_paid", "Tax paid", "total_tax_paid");

  const tb = tl.tax_paid_breakdown;
  const ctb = ctl?.tax_paid_breakdown;
  const breakdownChildren = [];
  if (tb && typeof tb === "object") {
    const labels = {
      cogs: "Tax on COGS",
      fees: "Tax on fees",
      advertising: "Tax on advertising",
      expenses: "Tax on expenses",
    };
    for (const [k, title] of Object.entries(labels)) {
      const v = pickRowNumber(tb, [k]);
      if (!Number.isFinite(v)) continue;
      breakdownChildren.push({
        key: `paid_${k}`,
        title,
        type: "currency",
        value: v,
        compareDelta: ctb ? pickRowNumber(ctb, [k]) : null,
      });
    }
  }

  const items = [];
  if (orderTax) items.push(orderTax);
  if (paidItem) {
    items.push({
      ...paidItem,
      children: breakdownChildren.length ? breakdownChildren : null,
    });
  }
  return items;
}

function daysInRange(start, end) {
  if (!start || !end) return 31;
  const d = dayjs(end).diff(dayjs(start), "day") + 1;
  return Math.min(500, Math.max(1, Number.isFinite(d) ? d : 31));
}

/**
 * Loads Dash analytics: chart time-series + summary aggregates (dashboard-optimized).
 * Both requests share marketplace, range, periodicity, SKU, compare, and filter_params.
 */
export default function useDashChartRows({
  userData,
  selectedMarketplace,
  otherRange,
  periodicity,
  selectedSkus,
  enabled,
  compareMeta,
  filterParams,
  isTaxInputCreditRecoverable = false,
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLabels, setSummaryLabels] = useState({});
  const [compareSummary, setCompareSummary] = useState(null);
  const [compareChange, setCompareChange] = useState(null);

  const skuKey = useMemo(() => (selectedSkus || []).filter(Boolean).join(","), [selectedSkus]);

  const filterParamsKey = filterParams == null || filterParams === "" ? "" : String(filterParams);

  const compareKey = useMemo(() => {
    if (!compareMeta?.enabled || !compareMeta?.start_date || !compareMeta?.end_date) return "";
    return `${compareMeta.start_date}|${compareMeta.end_date}`;
  }, [compareMeta?.enabled, compareMeta?.start_date, compareMeta?.end_date]);

  useEffect(() => {
    if (!enabled || !selectedMarketplace?.id || !userData?.token) {
      setLoading(false);
      setRows([]);
      setSummaryRows([]);
      setSummaryLabels({});
      setCompareSummary(null);
      setCompareChange(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const span = daysInRange(otherRange?.start_date, otherRange?.end_date);
    const perPage = Math.min(500, Math.max(span, 31));

    const base = {
      page: 1,
      "per-page": perPage,
      marketplace_id: selectedMarketplace.id,
      report_type: periodicity,
      start_date: otherRange?.start_date,
      end_date: otherRange?.end_date,
      sku: skuKey,
    };

    if (compareMeta?.enabled && compareMeta?.start_date && compareMeta?.end_date) {
      base.is_compare = "1";
      base.compare_with_start_date = compareMeta.start_date;
      base.compare_with_end_date = compareMeta.end_date;
    } else {
      base.is_compare = "0";
    }

    if (filterParamsKey) {
      base.filter_params = filterParamsKey;
    }

    const chartUrl = `dashboard-optimized/chart/get-chart-data${ConvertParams(base)}`;
    const summaryUrl = `dashboard-optimized/chart/get-summary-data${ConvertParams(base)}`;

    Promise.all([
      MakeApiCall(chartUrl, "get", null, true, {}, userData.token),
      MakeApiCall(summaryUrl, "get", null, true, {}, userData.token),
    ])
      .then(([chartRes, summaryRes]) => {
        if (cancelled) return;
        if (chartRes?.status) {
          setRows(normalizeChartRows(chartRes.data, periodicity));
        } else {
          setRows([]);
        }
        if (summaryRes?.status) {
          const normalized = normalizeSummaryPayload(summaryRes.data, periodicity);
          setSummaryRows(normalized.rows);
          setSummaryLabels(normalized.labels);
          setCompareSummary(normalized.compareSummary);
          setCompareChange(normalized.compareChange);
        } else {
          setSummaryRows([]);
          setSummaryLabels({});
          setCompareSummary(null);
          setCompareChange(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setSummaryRows([]);
          setSummaryLabels({});
          setCompareSummary(null);
          setCompareChange(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    userData?.token,
    selectedMarketplace?.id,
    otherRange?.start_date,
    otherRange?.end_date,
    periodicity,
    skuKey,
    compareKey,
    filterParamsKey,
  ]);

  const analytics = useMemo(() => {
    const seriesRows = rows;
    const aggRows = summaryRows.length ? summaryRows : rows;

    const net = extractSeries(seriesRows, ["net_profit"]);
    const gross = extractSeries(seriesRows, ["gross_profit"]);
    const ad = seriesRows.map((r) => {
      const ppc = r?.ppc_costs;
      if (ppc != null && Number.isFinite(Number(ppc))) return Math.abs(Number(ppc));
      return Math.abs(pickRowNumber(r, ["advertising_cost.total", "ppc_costs"]));
    });
    const payout = extractSeries(seriesRows, ["estimated_payout", "est_payout"]);
    const sales = seriesRows.map((r) => {
      if (r.sales != null && typeof r.sales === "object") return pickRowNumber(r, ["sales.total"]);
      return pickRowNumber(r, ["sales", "sales.total"]);
    });
    const orders = seriesRows.map((r) => pickRowNumber(r, ["total_orders", "orders"]));
    const units = seriesRows.map((r) => {
      if (r.units != null && typeof r.units === "object") return pickRowNumber(r, ["units.total"]);
      return pickRowNumber(r, ["units.total", "units", "units_sold"]);
    });
    const dates = seriesRows.map((r) => r.report_date || r.date || "");

    const summary = summaryRows.length === 1 ? summaryRows[0] : null;

    let totalSales = sumSeries(aggRows.map((r) => {
      if (r.sales != null && typeof r.sales === "object") return pickRowNumber(r, ["sales.total"]);
      return pickRowNumber(r, ["sales", "sales.total"]);
    }));
    let totalAd = sumSeries(
      aggRows.map((r) => {
        const ppc = r?.ppc_costs;
        if (ppc != null && Number.isFinite(Number(ppc))) return Math.abs(Number(ppc));
        return Math.abs(pickRowNumber(r, ["advertising_cost.total", "ppc_costs"]));
      })
    );
    let tacos = totalSales > 0 ? (totalAd / totalSales) * 100 : 0;
    let netTotal = sumSeries(extractSeries(aggRows, ["net_profit"]));
    let marginNet = totalSales > 0 ? (netTotal / totalSales) * 100 : 0;
    let grossTotal = sumSeries(extractSeries(aggRows, ["gross_profit"]));
    let marginGross = totalSales > 0 ? (grossTotal / totalSales) * 100 : 0;
    let payoutTotal = sumSeries(extractSeries(aggRows, ["estimated_payout", "est_payout"]));

    let ordersTotal = sumSeries(aggRows.map((r) => pickRowNumber(r, ["total_orders", "orders"])));
    let unitsTotalAgg = sumSeries(
      aggRows.map((r) => {
        if (r.units != null && typeof r.units === "object") return pickRowNumber(r, ["units.total"]);
        return pickRowNumber(r, ["units.total", "units", "units_sold"]);
      })
    );

    const adSalesPerRow = aggRows.map((r) => {
      const t = pickRowNumber(r, ["advertising_sales.total", "ad_sales"]);
      if (t > 0) return t;
      return (
        pickRowNumber(r, ["advertising_sales.sp_sales"]) +
        pickRowNumber(r, ["advertising_sales.sb_sales"]) +
        pickRowNumber(r, ["advertising_sales.sd_sales"])
      );
    });
    let adSalesTotal = sumSeries(adSalesPerRow);
    let roas = totalAd > 0 ? adSalesTotal / totalAd : 0;

    const refundUnits = aggRows.map((r) => pickRowNumber(r, ["refund_units", "total_refunds"]));
    let refundUnitsTotal = sumSeries(refundUnits);
    let refundRatePct = unitsTotalAgg > 0 ? (refundUnitsTotal / unitsTotalAgg) * 100 : 0;

    const clicks = aggRows.map((r) => pickRowNumber(r, ["advertising_clicks.total", "ad_clicks"]));
    const adUnitsPerRow = aggRows.map((r) => {
      const advertisingUnits = pickRowNumber(r, ["advertising_units.total"]);
      if (advertisingUnits > 0) return advertisingUnits;
      const nested =
        pickRowNumber(r, ["units.sp_units"]) +
        pickRowNumber(r, ["units.sb_units"]) +
        pickRowNumber(r, ["units.sd_units"]);
      if (nested > 0) return nested;
      return pickRowNumber(r, ["ad_units"]);
    });
    let clicksTotal = sumSeries(clicks);
    let adCvrAgg = clicksTotal > 0 ? (sumSeries(adUnitsPerRow) / clicksTotal) * 100 : 0;

    const organicPerRow = aggRows.map((r) => {
      if (r.sales != null && typeof r.sales === "object") return pickRowNumber(r, ["sales.organic"]);
      return pickRowNumber(r, ["sales.organic", "organic"]);
    });
    let organicSalesTotal = sumSeries(organicPerRow);
    const impressionsPerRow = aggRows.map((r) =>
      pickRowNumber(r, ["advertising_impressions.total", "impressions", "ad_impressions"])
    );
    let impressionsTotal = sumSeries(impressionsPerRow);
    let aovValue = 0;

    if (summary) {
      totalSales = pickRowNumber(summary, ["sales.total", "sales"]);
      totalAd = Math.abs(pickRowNumber(summary, ["advertising_cost.total", "ppc_costs"]));
      tacos = pickRowNumber(summary, ["TACoS", "tacos"]);
      netTotal = pickRowNumber(summary, ["net_profit"]);
      marginNet = pickRowNumber(summary, ["profit_percent", "margin"]);
      grossTotal = pickRowNumber(summary, ["gross_profit"]);
      marginGross = totalSales > 0 ? (grossTotal / totalSales) * 100 : 0;
      payoutTotal = pickRowNumber(summary, ["est_payout", "estimated_payout"]);
      ordersTotal = pickRowNumber(summary, ["total_orders", "orders"]);
      unitsTotalAgg = pickRowNumber(summary, ["units.total", "units", "units_sold"]);
      adSalesTotal = pickRowNumber(summary, ["advertising_sales.total", "ad_sales"]);
      roas = pickRowNumber(summary, ["roas"]);
      refundUnitsTotal = pickRowNumber(summary, ["refund_units", "total_refunds"]);
      refundRatePct = pickRowNumber(summary, ["refunds_percent"]);
      clicksTotal = pickRowNumber(summary, ["advertising_clicks.total", "ad_clicks"]);
      adCvrAgg = pickRowNumber(summary, ["cvr", "ad_cvr"]);
      organicSalesTotal = pickRowNumber(summary, ["sales.organic"]);
      impressionsTotal = pickRowNumber(summary, [
        "advertising_impressions.total",
        "impressions",
        "ad_impressions",
      ]);
      aovValue = pickRowNumber(summary, ["avg_selling_price", "aov"]);
    }

    const acosDisplay = summary
      ? pickRowNumber(summary, ["acos"])
      : adSalesTotal > 0
        ? (totalAd / adSalesTotal) * 100
        : 0;
    const cpcDisplay = summary
      ? pickRowNumber(summary, ["cpc"])
      : clicksTotal > 0
        ? totalAd / clicksTotal
        : 0;
    const adAttributionDisplay = summary
      ? pickRowNumber(summary, ["ad_attribution"])
      : totalSales > 0
        ? (adSalesTotal / totalSales) * 100
        : 0;
    const ctrDisplay = summary ? pickRowNumber(summary, ["ctr"]) : 0;

    if (impressionsTotal <= 0 && clicksTotal > 0 && ctrDisplay > 0) {
      impressionsTotal = Math.round(clicksTotal / (ctrDisplay / 100));
    }

    const hasCompare = !!compareMeta?.enabled && !!compareSummary;
    const prevSales = hasCompare ? pickRowNumber(compareSummary, ["sales.total", "sales"]) : 0;
    const prevNet = hasCompare ? pickRowNumber(compareSummary, ["net_profit"]) : 0;
    const prevGross = hasCompare ? pickRowNumber(compareSummary, ["gross_profit"]) : 0;
    const prevAd = hasCompare
      ? Math.abs(pickRowNumber(compareSummary, ["advertising_cost.total", "ppc_costs"]))
      : 0;
    const prevPayout = hasCompare
      ? pickRowNumber(compareSummary, ["est_payout", "estimated_payout"])
      : 0;
    const prevOrders = hasCompare ? pickRowNumber(compareSummary, ["total_orders", "orders"]) : 0;
    const prevAdSales = hasCompare
      ? pickRowNumber(compareSummary, ["advertising_sales.total", "ad_sales"])
      : 0;
    const prevRefundRate = hasCompare ? pickRowNumber(compareSummary, ["refunds_percent"]) : 0;
    const prevAdCvr = hasCompare ? pickRowNumber(compareSummary, ["cvr", "ad_cvr"]) : 0;
    const prevAcos = hasCompare ? pickRowNumber(compareSummary, ["acos"]) : 0;
    const prevCpc = hasCompare ? pickRowNumber(compareSummary, ["cpc"]) : 0;
    const prevAdAttribution = hasCompare ? pickRowNumber(compareSummary, ["ad_attribution"]) : 0;
    const prevTacosKpi = hasCompare ? pickRowNumber(compareSummary, ["TACoS", "tacos"]) : 0;
    const prevOrganic = hasCompare ? pickRowNumber(compareSummary, ["sales.organic"]) : 0;
    const prevUnits = hasCompare ? pickRowNumber(compareSummary, ["units.total", "units", "units_sold"]) : 0;
    const prevRefundUnits = hasCompare ? pickRowNumber(compareSummary, ["refund_units", "total_refunds"]) : 0;
    const prevCtr = hasCompare ? pickRowNumber(compareSummary, ["ctr"]) : 0;
    const prevImpressions = hasCompare
      ? pickRowNumber(compareSummary, ["advertising_impressions.total", "impressions", "ad_impressions"])
      : 0;
    const prevAov = hasCompare ? pickRowNumber(compareSummary, ["avg_selling_price", "aov"]) : 0;
    const prevRoas = hasCompare ? pickRowNumber(compareSummary, ["roas"]) : 0;
    const prevClicks = hasCompare
      ? pickRowNumber(compareSummary, ["advertising_clicks.total", "ad_clicks"])
      : 0;

    const compareTrends = hasCompare
      ? {
          sales: compareTrend(prevSales, compareChange, ["sales_total", "sales"]),
          net: compareTrend(prevNet, compareChange, ["net_profit"]),
          gross: compareTrend(prevGross, compareChange, ["gross_profit"]),
          ad: compareTrend(prevAd, compareChange, ["advertising_cost_total", "ppc_costs"]),
          payout: compareTrend(prevPayout, compareChange, ["est_payout", "estimated_payout"]),
          orders: compareTrend(prevOrders, compareChange, ["total_orders", "orders"]),
          adSales: compareTrend(prevAdSales, compareChange, ["advertising_sales_total", "ad_sales"]),
          refundRate: compareTrend(prevRefundRate, compareChange, ["refund_rate", "refunds_percent"]),
          adCvr: compareTrend(prevAdCvr, compareChange, ["cvr", "ad_cvr"]),
          acosKpi: compareTrend(prevAcos, compareChange, ["acos"]),
          cpcKpi: compareTrend(prevCpc, compareChange, ["cpc"]),
          adAttribution: compareTrend(prevAdAttribution, compareChange, ["ad_attribution"]),
          tacosKpi: compareTrend(prevTacosKpi, compareChange, ["TACoS", "tacos", "tacos_percent"]),
          organicSales: compareTrend(prevOrganic, compareChange, ["organic_sales", "sales_organic"]),
          unitsSold: compareTrend(prevUnits, compareChange, ["units_total", "units_sold"]),
          refundUnits: compareTrend(prevRefundUnits, compareChange, ["refund_units"]),
          clicks: compareTrend(prevClicks, compareChange, ["advertising_clicks_total", "ad_clicks"]),
          impressions: compareTrend(prevImpressions, compareChange, ["impressions", "advertising_impressions_total"]),
          ctr: compareTrend(prevCtr, compareChange, ["ctr"]),
          aov: compareTrend(prevAov, compareChange, ["avg_selling_price", "aov"]),
          roas: compareTrend(prevRoas, compareChange, ["roas"]),
        }
      : {};

    const revenueKpis = {
      ordersTotal,
      unitsTotalAgg,
      ordersTrend: compareTrends.orders || null,
      adSalesTotal,
      roas,
      adSalesTrend: compareTrends.adSales || null,
      refundRatePct,
      refundUnitsTotal,
      refundTrend: compareTrends.refundRate || null,
      adCvrPct: adCvrAgg,
      clicksTotal,
      adCvrTrend: compareTrends.adCvr || null,
      acosPct: acosDisplay,
      cpcValue: cpcDisplay,
      adAttributionPct: adAttributionDisplay,
      ctrPct: ctrDisplay,
      tacosPct: tacos,
      acosTrend: compareTrends.acosKpi || null,
      cpcTrend: compareTrends.cpcKpi || null,
      adAttributionTrend: compareTrends.adAttribution || null,
      tacosTrend: compareTrends.tacosKpi || null,
      organicSalesTotal,
      organicSalesTrend: compareTrends.organicSales || null,
      totalSalesTotal: totalSales,
      totalSalesTrend: compareTrends.sales || null,
      adSpendTotal: totalAd,
      adSpendTrend: compareTrends.ad || null,
      impressionsTotal,
      impressionsTrend: compareTrends.impressions || null,
      unitsSoldTrend: compareTrends.unitsSold || null,
      refundUnitsTrend: compareTrends.refundUnits || null,
      clicksTrend: compareTrends.clicks || null,
      ctrTrend: compareTrends.ctr || null,
      aovValue,
      aovTrend: compareTrends.aov || null,
      roasTrend: compareTrends.roas || null,
    };

    const cogsPerRow = aggRows.map((r) =>
      r.cost_of_goods != null && typeof r.cost_of_goods === "object"
        ? pickRowNumber(r, ["cost_of_goods.total"])
        : pickRowNumber(r, ["cost_of_goods.total", "total_cogs"])
    );
    const amazonFees = Math.abs(
      sumSeries(aggRows.map((r) => pickRowNumber(r, ["amazon_fees.total", "amazon_fees"])))
    );
    const costOfGoods = Math.abs(sumSeries(cogsPerRow));
    const advertising = Math.abs(totalAd);
    const netTax = summary
      ? pickRowNumber(summary, ["tax_liability.total", "net_tax"])
      : sumSeries(aggRows.map((r) => pickRowNumber(r, ["tax_liability.total", "net_tax"])));
    const expenses = Math.abs(
      sumSeries(
        aggRows.map((r) => pickRowNumber(r, ["expenses.total", "expenses", "total_expenses"]))
      )
    );

    const prevAmazonFees = hasCompare
      ? Math.abs(pickRowNumber(compareSummary, ["amazon_fees.total", "amazon_fees"]))
      : 0;
    const prevCostOfGoods = hasCompare
      ? Math.abs(pickRowNumber(compareSummary, ["cost_of_goods.total", "total_cogs"]))
      : 0;
    const prevAdvertising = hasCompare
      ? Math.abs(pickRowNumber(compareSummary, ["advertising_cost.total", "ppc_costs"]))
      : 0;
    const prevNetTax = hasCompare
      ? pickRowNumber(compareSummary, ["tax_liability.total", "net_tax"])
      : 0;
    const prevExpenses = hasCompare
      ? Math.abs(pickRowNumber(compareSummary, ["expenses.total", "expenses", "total_expenses"]))
      : 0;

    const costBreakdownItems = [
      {
        key: "amazon_fees",
        label: "Amazon Fees",
        color: "#f1416c",
        amount: amazonFees,
        compare: hasCompare
          ? compareTrend(prevAmazonFees, compareChange, ["amazon_fees_total", "amazon_fees"])
          : null,
      },
      {
        key: "cogs",
        label: "Cost of Goods",
        color: "#6D4C41",
        amount: costOfGoods,
        compare: hasCompare
          ? compareTrend(prevCostOfGoods, compareChange, ["cost_of_goods_total", "total_cogs"])
          : null,
      },
      {
        key: "advertising",
        label: "Advertising",
        color: "#D4A574",
        amount: advertising,
        compare: hasCompare
          ? compareTrend(prevAdvertising, compareChange, ["advertising_cost_total", "ppc_costs"])
          : null,
      },
      {
        key: "net_tax",
        label: "Net Tax",
        color: "#7239ea",
        amount: netTax,
        compare: hasCompare
          ? compareTrend(prevNetTax, compareChange, ["tax_liability_total", "net_tax"])
          : null,
      },
      {
        key: "expenses",
        label: "Expenses",
        color: "#A1A5B7",
        amount: expenses,
        compare: hasCompare
          ? compareTrend(prevExpenses, compareChange, ["expenses_total", "total_expenses"])
          : null,
      },
    ];
    const costBreakdownTotal = costBreakdownItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    const labelTitle = (key, fallback) => summaryLabels?.[key]?.title || fallback;

    const prevClicksTotal = hasCompare ? pickRowNumber(compareSummary, ["advertising_clicks.total", "ad_clicks"]) : 0;
    const refundCostAbs = summary
      ? Math.abs(pickRowNumber(summary, ["refund_cost.total", "refund_cost"]))
      : Math.abs(
          sumSeries(aggRows.map((r) => pickRowNumber(r, ["refund_cost.total", "refund_cost"])))
        );
    const prevRefundCostAbs = hasCompare
      ? Math.abs(pickRowNumber(compareSummary || {}, ["refund_cost.total", "refund_cost"]))
      : 0;

    const chSales = summary ? plSalesChildren(summaryLabels, summary, compareSummary) : [];
    const chFees = summary ? plAmazonFeesChildren(summaryLabels, summary, compareSummary) : [];
    const chCogs = summary ? plCogsChildren(summaryLabels, summary, compareSummary) : [];
    const chRefund = summary ? plRefundCostChildren(summaryLabels, summary, compareSummary) : [];
    const chPpc = summary ? plPpcCostChildren(summaryLabels, summary, compareSummary) : [];
    const chClicks = summary ? plAdClicksChildren(summary, compareSummary) : [];
    const chExp = summary ? plExpensesChildren(summaryLabels, summary, compareSummary) : [];
    const chTax = summary
      ? plTaxLiabilityChildren(summaryLabels, summary, compareSummary, isTaxInputCreditRecoverable)
      : [];

    const profitLossRows = [
      {
        key: "total_sales",
        kind: "revenue",
        label: "Total Revenue",
        subtitle:
          ordersTotal > 0
            ? `${labelTitle("sales", "Sales")} · ${ordersTotal} orders`
            : labelTitle("sales", "Sales"),
        amount: totalSales,
        amountFormat: "currency",
        compare: hasCompare ? compareTrend(prevSales, compareChange, ["sales_total", "sales"]) : null,
        children: chSales.length ? chSales : null,
      },
      {
        key: "cost_of_goods",
        kind: "cost",
        label: "Cost of Goods Sold",
        subtitle:
          unitsTotalAgg > 0
            ? `${unitsTotalAgg} units · ${labelTitle("total_cogs", "COGS")}`
            : labelTitle("total_cogs", "Cost of goods sold"),
        amount: -costOfGoods,
        amountFormat: "currency",
        compare: hasCompare ? compareTrend(-prevCostOfGoods, compareChange, ["cost_of_goods_total", "total_cogs"]) : null,
        children: chCogs.length ? chCogs : null,
      },
      {
        key: "amazon_fees",
        kind: "cost",
        label: "Amazon Fees",
        subtitle: totalSales > 0 ? `${((amazonFees / totalSales) * 100).toFixed(2)}% of revenue` : labelTitle("fba_fees", "FBA & referral"),
        amount: -amazonFees,
        amountFormat: "currency",
        compare: hasCompare ? compareTrend(-prevAmazonFees, compareChange, ["amazon_fees_total", "amazon_fees"]) : null,
        children: chFees.length ? chFees : null,
      },
      {
        key: "refund_cost",
        kind: "cost",
        label: labelTitle("refund_cost", "Refund Cost"),
        subtitle: `${refundUnitsTotal} units · ${refundRatePct.toFixed(2)}% refund rate`,
        amount: -refundCostAbs,
        amountFormat: "currency",
        compare: hasCompare
          ? compareTrend(-prevRefundCostAbs, compareChange, ["refund_cost"])
          : null,
        children: chRefund.length ? chRefund : null,
      },
      {
        key: "advertising_cost",
        kind: "cost",
        label: "Ad Spend",
        subtitle: totalSales > 0 ? `TACoS ${tacos.toFixed(2)}%` : "",
        amount: -advertising,
        amountFormat: "currency",
        compare: hasCompare ? compareTrend(-prevAdvertising, compareChange, ["advertising_cost_total", "ppc_costs"]) : null,
        children: chPpc.length ? chPpc : null,
      },
      {
        key: "gross_profit",
        kind: "gross",
        label: labelTitle("gross_profit", "Gross Profit"),
        subtitle: "Before tax & overhead",
        amount: grossTotal,
        amountFormat: "currency",
        margin: totalSales > 0 ? (grossTotal / totalSales) * 100 : 0,
        compare: hasCompare ? compareTrend(prevGross, compareChange, ["gross_profit"]) : null,
        children: null,
      },
      // {
      //   key: "advertising_clicks",
      //   kind: "metric",
      //   label: "Advertising Clicks",
      //   subtitle: adCvrAgg > 0 ? `CVR ${adCvrAgg.toFixed(2)}%` : "Sponsored traffic",
      //   amount: clicksTotal,
      //   amountFormat: "number",
      //   compare: hasCompare ? compareTrend(prevClicksTotal, compareChange, ["advertising_clicks_total", "ad_clicks"]) : null,
      //   children: chClicks.length ? chClicks : null,
      // },
      {
        key: "expenses",
        kind: "cost",
        label: "Operating Expenses",
        subtitle: labelTitle("general_costs", "Operating overhead"),
        amount: -expenses,
        amountFormat: "currency",
        compare: hasCompare ? compareTrend(-prevExpenses, compareChange, ["expenses_total", "total_expenses"]) : null,
        children: chExp.length ? chExp : null,
      },
      ...(hasTaxLiability(summary)
        ? [
            {
              key: "net_tax",
              kind: "cost",
              label: "Net Tax (GST)",
              subtitle: labelTitle("tax", "Tax liability"),
              amount: netTax,
              amountFormat: "currency",
              compare: hasCompare
                ? compareTrend(prevNetTax, compareChange, ["tax_liability_total", "net_tax"])
                : null,
              children: chTax.length ? chTax : null,
            },
          ]
        : []),
    ];

    return {
      rows,
      summaryRows,
      summaryLabels,
      series: { net, gross, ad, payout, sales, orders, units, dates },
      totals: {
        sales: totalSales,
        net: netTotal,
        gross: grossTotal,
        ad: totalAd,
        payout: payoutTotal,
        tacos,
        marginNet,
        marginGross,
      },
      trends: {
        net: compareTrends.net || null,
        gross: compareTrends.gross || null,
        ad: compareTrends.ad || null,
        payout: compareTrends.payout || null,
        sales: compareTrends.sales || null,
      },
      revenueKpis,
      costBreakdown: {
        items: costBreakdownItems,
        total: costBreakdownTotal,
      },
      profitLoss: {
        periodLabel: periodicity === "month" ? "Monthly" : "MTD",
        compareLabel: compareMeta?.enabled ? "vs prior period" : "",
        rows: profitLossRows,
        netProfit: {
          label: labelTitle("net_profit", "Net Profit"),
          amount: netTotal,
          margin: marginNet,
          payout: payoutTotal,
          compare: hasCompare ? compareTrend(prevNet, compareChange, ["net_profit"]) : null,
        },
      },
    };
  }, [rows, summaryRows, summaryLabels, compareSummary, compareChange, compareMeta?.enabled, periodicity, isTaxInputCreditRecoverable]);

  return { loading, rows, ...analytics };
}
